/**
 * SwordFight Web Component - Refactored Architecture
 *
 * @description
 * Main web component for the SwordFight game with clean separation of concerns.
 * Uses a centralized state manager with unidirectional data flow.
 *
 * @extends HTMLElement
 */

// ============================================================================
// Dependencies
// ============================================================================
import { Modal } from 'bootstrap';
import { DurableObjectTransport } from 'swordfight-engine/transports/DurableObjectTransport';
import './character-card.js';

// Engine imports
import * as FullEngine from 'swordfight-engine/full';
import * as LiteEngine from 'swordfight-engine/lite';

const ENGINE_VERSION = process.env.ELEVENTY_ENV === 'production' ? 'lite' : 'full';
const engine = process.env.ELEVENTY_ENV === 'production' ? LiteEngine : FullEngine;
const { Game, BonusCalculator } = engine;

// ============================================================================
// Flavor Text
// ============================================================================

let flavorTextData = null;
const loadFlavorText = async () => {
  if (!flavorTextData) {
    const response = await fetch('/flavor-text.json');
    flavorTextData = await response.json();
  }
  return flavorTextData;
};

const randomChoice = (array) => {
  if (!array || array.length === 0) return '';
  return array[Math.floor(Math.random() * array.length)];
};

// ============================================================================
// Configuration
// ============================================================================

const CONFIG = {
  MULTIPLAYER_SERVER_URL: 'wss://swordfight-multiplayer.michael-delaney.workers.dev',
  PRODUCTION_HOSTNAME: 'swordfight.me',
  GAME_ID_LENGTH: 5,
  STORAGE_KEYS: {
    PLAYER_NAME: 'playerName',
    CHARACTER: 'myCharacter',
    COMPUTER_OPPONENT: 'computerOpponent',
    GAME_IN_PROGRESS: 'gameInProgress'
  }
};

// ============================================================================
// PHASE 1: Simplified State - Engine as Source of Truth
// ============================================================================

/**
 * StateManager - Minimal wrapper around engine state
 * The engine (this.Game) IS the source of truth for game data
 * This only manages UI state and provides convenience methods
 */
class StateManager {
  constructor() {
    // Only store what the engine doesn't manage
    this.ui = {
      animating: false,
      invite: null,
      flowType: null,
      selectedGameMode: null
    };

    // Round history for combat log (engine may not persist this)
    this.roundHistory = [];

    // Event listeners for round completion
    this.roundListeners = [];

    // Track previous range to detect changes
    this.previousRange = 'far';

    // The engine Game instance (set during initialization)
    this.game = null;
    this.gameId = null;
    this.gameMode = null; // 'computer' or 'multiplayer'
  }

  /**
   * Subscribe to round completion
   */
  onRoundComplete(callback) {
    this.roundListeners.push(callback);
  }

  /**
   * Trigger round completion and await all listeners
   */
  async triggerRoundComplete(roundData) {
    this.roundHistory.push(roundData);

    // Await each listener to ensure animations complete
    for (const cb of this.roundListeners) {
      await cb(roundData);
    }
  }

  /**
   * Check if game is over (delegates to engine)
   */
  isGameOver() {
    return this.game?.gameOver || false;
  }

  /**
   * Get winner from engine state
   */
  getWinner() {
    if (!this.game) return null;
    if (this.game.myCharacter?.health <= 0) return 'opponent';
    if (this.game.opponentsCharacter?.health <= 0) return 'player';
    return null;
  }
}

// ============================================================================
// PHASE 2: UI Renderer - Reads state, updates DOM (one-way)
// ============================================================================

/**
 * UIRenderer - Handles all DOM updates reading from engine state
 * Engine (game) is the source of truth
 */
class UIRenderer {
  constructor(shadowRoot, stateManager) {
    this.shadowRoot = shadowRoot;
    this.state = stateManager; // Minimal UI state only

    // Cache DOM references
    this.elements = {
      intro: shadowRoot.getElementById('Intro'),
      game: shadowRoot.getElementById('Game'),
      characters: shadowRoot.getElementById('Characters'),
      rangeStrip: shadowRoot.getElementById('RangeStrip'),
      rounds: shadowRoot.getElementById('Rounds'),
      myMovesElement: shadowRoot.getElementById('myMovesGrouped'),
      gameLogBodyElement: shadowRoot.getElementById('gameLogBody'),
      movesDrawerBody: shadowRoot.getElementById('movesDrawerBody')
    };
  }

  /**
   * Render mini-log on discard pile
   */
  renderMiniLog(isMyCharacter) {
    const prefix = isMyCharacter ? 'my' : 'opponents';
    const discardPile = this.shadowRoot.querySelector(`#${prefix}DiscardPile`);
    if (!discardPile) return;

    const miniLog = discardPile.querySelector('.mini-log');
    if (!miniLog) return;

    const history = this.state.roundHistory;
    if (history.length === 0) return;

    const lastRound = history[history.length - 1];
    const game = this.state.game;
    const char = isMyCharacter ? game.myCharacter : game.opponentsCharacter;
    const move = isMyCharacter ? lastRound.myMove : lastRound.opponentMove;
    const damage = isMyCharacter ? lastRound.results.myDamage : lastRound.results.opponentDamage;
    const damageTaken = isMyCharacter ? lastRound.results.opponentDamage : lastRound.results.myDamage;
    const effects = isMyCharacter ? lastRound.results.myEffects : lastRound.results.opponentEffects;

    // Wrap move name in span with data attributes for color coding
    const moveNameSpan = `<span class="move-name" data-type="${move.type || ''}" data-tag="${move.tag || ''}" data-range="${move.range || ''}">${move.name}</span>`;

    let logText = `${isMyCharacter ? 'You' : char.playerName || 'Opponent'} did a ${moveNameSpan}`;

    if (damage > 0) {
      logText += ` 🎯 Score: ${damage}`;
    }

    if (effects?.selfDamage && effects.selfDamage > 0) {
      logText += ` ⚠️ Self-damage: ${effects.selfDamage}`;
    }

    if (effects?.heal && effects.heal > 0) {
      logText += ` 💚 Heal: ${effects.heal}`;
    }

    if (damageTaken > 0) {
      logText += ` 💥 Took ${damageTaken} damage`;
    }

    miniLog.innerHTML = logText;
  }

  /**
   * Render character card from engine state
   */
  renderCharacter(isMyCharacter) {
    const game = this.state.game;
    if (!game) return null;

    const char = isMyCharacter ? game.myCharacter : game.opponentsCharacter;
    if (!char) return null;

    const prefix = isMyCharacter ? 'my' : 'opponents';
    const wrapperId = `${prefix}CharacterWrapper`;

    // Get or create wrapper
    let wrapper = this.shadowRoot.querySelector(`#${wrapperId}`);
    if (!wrapper) {
      const template = document.getElementById('gameCharacterTemplate');
      const clone = document.importNode(template.content, true);
      wrapper = clone.querySelector('.character-card-wrapper');
      wrapper.id = wrapperId;
      wrapper.querySelector('.character-card').id = `${prefix}CharacterCard`;
      wrapper.classList.add(`${prefix}-wrapper`);
    }

    const card = wrapper.querySelector('.character-card');

    // Populate basic info
    const playerName = char.playerName || (isMyCharacter ? 'You' : 'Opponent');
    const characterName = char.name || '';
    const characterIcon = char.icon || '';

    // Populate card
    card.querySelector('.character-name').textContent = (characterIcon ? characterIcon + ' ' : '') + characterName;
    const badge = card.querySelector('.player-badge');
    badge.textContent = playerName ? `👤 ${playerName}` : 'Waiting for player...';
    badge.className = playerName ? 'player-badge' : 'player-badge no-player-name';

    // Equipment
    card.querySelector('.weapon .equipment-value').textContent = char.weapon || 'None';
    card.querySelector('.shield .equipment-value').textContent = char.shield || 'None';
    card.querySelector('.weapon').className = char.weapon ? 'equipment-item-inline weapon has-item' : 'equipment-item-inline weapon no-item';
    card.querySelector('.shield').className = char.shield ? 'equipment-item-inline shield has-item' : 'equipment-item-inline shield no-item';

    // Set data attributes for weapon/shield status (for CSS styling)
    card.setAttribute('data-weapon', char.weapon ? 'true' : 'false');
    card.setAttribute('data-shield', char.shield ? 'true' : 'false');

    // Health
    const health = char.health || 0;
    const startingHealth = char.startingHealth || char.health || 100;
    const healthPercentage = Math.round((health / startingHealth) * 100);
    const healthBarColor = healthPercentage > 66 ? 'bg-success' : healthPercentage > 33 ? 'bg-warning' : 'bg-danger';

    card.querySelector('.health-value').textContent = `${health} / ${startingHealth}`;
    const progressBar = card.querySelector('.progress-bar');
    progressBar.className = `progress-bar ${healthBarColor}`;
    progressBar.style.width = `${healthPercentage}%`;
    progressBar.textContent = `${health}`;

    // Current move section - show the RESULT from opponent's roundData (swap pattern)
    const currentMoveSection = wrapper.querySelector('.current-move-section');
    const roundData = isMyCharacter ? game.opponentsRoundData : game.myRoundData;

    // Find or create the placeholder/content area (skip the sticky-tags)
    let contentArea = currentMoveSection.querySelector('.current-move-placeholder, .current-move');
    
    if (roundData?.result?.name) {
      const hit = roundData?.score ? true : false;
      const moveHTML = `
        <div class="current-move ${hit ? 'hit' : ''}">
          <div class="move-name">🎯 ${roundData.result.name}</div>
          ${roundData.range || roundData.totalScore ? `
            <div class="move-details">
              ${roundData.range ? `Range: ${roundData.range}` : ''}
              ${roundData.range && roundData.totalScore ? ' • ' : ''}
              ${roundData.totalScore ? `Score: ${roundData.totalScore}` : ''}
            </div>
          ` : ''}
        </div>
      `;
      
      if (contentArea) {
        contentArea.outerHTML = moveHTML;
      } else {
        // If no content area exists, append it after sticky-tags
        const tags = currentMoveSection.querySelectorAll('.sticky-tags');
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = moveHTML;
        if (tags.length > 0) {
          tags[tags.length - 1].insertAdjacentElement('afterend', tempDiv.firstElementChild);
        } else {
          currentMoveSection.appendChild(tempDiv.firstElementChild);
        }
      }
    } else {
      const placeholderHTML = '<div class="current-move-placeholder">Waiting for action...</div>';
      if (contentArea) {
        contentArea.outerHTML = placeholderHTML;
      } else {
        // If no content area exists, append it after sticky-tags
        const tags = currentMoveSection.querySelectorAll('.sticky-tags');
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = placeholderHTML;
        if (tags.length > 0) {
          tags[tags.length - 1].insertAdjacentElement('afterend', tempDiv.firstElementChild);
        } else {
          currentMoveSection.appendChild(tempDiv.firstElementChild);
        }
      }
    }

    // Apply character color
    if (char.color) {
      const headerEl = card.querySelector('.card-header-custom');
      if (headerEl) headerEl.style.setProperty('--character-color', char.color);
      card.style.setProperty('--character-color', char.color);
    }

    // Defeat icon
    const defeatIcon = card.querySelector('.defeat-icon');
    if (defeatIcon && characterIcon) {
      defeatIcon.textContent = characterIcon;
    }

    return wrapper;
  }

  /**
   * Render restrictions from round data (swapped: opponent's restrictions affect my character)
   */
  renderRestrictions(isMyCharacter) {
    const game = this.state.game;
    if (!game) return;

    // Swap roundData: opponent's roundData shows restrictions on my character
    const roundData = isMyCharacter ? game.opponentsRoundData : game.myRoundData;
    if (!roundData) return;

    const prefix = isMyCharacter ? 'my' : 'opponents';
    const wrapper = this.shadowRoot.querySelector(`#${prefix}CharacterWrapper`);
    if (!wrapper) return;

    const container = wrapper.querySelector('.restrictions-tags');
    if (!container) return;

    container.innerHTML = '';
    const template = document.getElementById('stickyTagTemplate');
    if (!template) return;

    const restrictions = [];

    // Add restrictions from roundData
    if (roundData.restrictions) {
      roundData.restrictions.forEach(restriction => {
        restrictions.push({ text: restriction, type: 'restrict' });
      });
    }

    // Add allowOnly restrictions
    if (roundData.result?.allowOnly) {
      roundData.result.allowOnly.forEach(allowOnly => {
        restrictions.push({ text: `Only ${allowOnly}`, type: 'allow' });
      });
    }

    restrictions.forEach(restriction => {
      const clone = document.importNode(template.content, true);
      const tag = clone.querySelector('.sticky-tag');
      tag.textContent = `🚫 ${restriction.text}`;
      tag.className = 'sticky-tag restriction';
      tag.setAttribute('data-type', restriction.type);
      container.appendChild(tag);
    });
  }

  /**
   * Render bonuses from round data (swapped: opponent's bonuses affect my character)
   */
  renderBonuses(isMyCharacter) {
    const game = this.state.game;
    if (!game) return;

    // Swap roundData: opponent's roundData shows bonuses on my character
    const roundData = isMyCharacter ? game.opponentsRoundData : game.myRoundData;
    if (!roundData) return;

    const prefix = isMyCharacter ? 'my' : 'opponents';
    const wrapper = this.shadowRoot.querySelector(`#${prefix}CharacterWrapper`);
    if (!wrapper) return;

    const container = wrapper.querySelector('.bonuses-tags');
    if (!container) return;

    container.innerHTML = '';
    const template = document.getElementById('stickyTagTemplate');
    if (!template) return;

    const bonuses = [];

    if (roundData.nextRoundBonus) {
      roundData.nextRoundBonus.forEach(bonusObj => {
        for (const [type, value] of Object.entries(bonusObj)) {
          bonuses.push({ type, value });
        }
      });
    }

    bonuses.forEach(bonus => {
      const clone = document.importNode(template.content, true);
      const tag = clone.querySelector('.sticky-tag');
      const sign = bonus.value > 0 ? '+' : '';
      tag.textContent = `⭐ ${sign}${bonus.value} ${bonus.type}`;
      tag.className = 'sticky-tag bonus';
      tag.setAttribute('data-type', bonus.type);
      container.appendChild(tag);
    });
  }

  /**
   * Render entire game view from state
   */
  renderGameView() {
    this.elements.game.style.display = 'block';
    this.elements.intro.style.display = 'none';

    // Clear and re-render characters
    this.elements.characters.innerHTML = '';
    const myWrapper = this.renderCharacter(true);
    const opponentWrapper = this.renderCharacter(false);
    this.elements.characters.appendChild(myWrapper);
    this.elements.characters.appendChild(opponentWrapper);

    // Render restrictions and bonuses
    this.renderRestrictions(true);
    this.renderRestrictions(false);
    this.renderBonuses(true);
    this.renderBonuses(false);

    // Render range strip
    this.renderRangeStrip();
  }

  /**
   * Render range strip with character positions
   */
  renderRangeStrip() {
    if (!this.elements.rangeStrip || !this.state.game) return;

    const game = this.state.game;
    const range = game.range || 'far'; // Default to far if not set
    
    // Get character icons
    const myIconText = game.myCharacter?.icon || '🤺';
    const opponentIconText = game.opponentsCharacter?.icon || '🤺';

    // Calculate positions (1-6, where 1 is leftmost)
    const positions = this._calculateRangePositions(range);

    // Update icons
    const playerIconEl = this.elements.rangeStrip.querySelector('.player-icon');
    const opponentIconEl = this.elements.rangeStrip.querySelector('.opponent-icon');

    if (playerIconEl) {
      playerIconEl.textContent = myIconText;
      playerIconEl.dataset.position = positions.player;
      this._updateIconPosition(playerIconEl, positions.player);
    }

    if (opponentIconEl) {
      opponentIconEl.textContent = opponentIconText;
      opponentIconEl.dataset.position = positions.opponent;
      this._updateIconPosition(opponentIconEl, positions.opponent);
    }

    // Mark occupied squares
    const squares = this.elements.rangeStrip.querySelectorAll('.range-square');
    squares.forEach((square, index) => {
      const pos = index + 1;
      square.classList.toggle('occupied', pos === positions.player || pos === positions.opponent);
    });
  }

  /**
   * Calculate character positions based on range
   * Layout: Far(1) - Med(2) - Close(3-4) - Med(5) - Far(6)
   */
  _calculateRangePositions(range) {
    const positions = {
      player: 1,
      opponent: 6
    };

    switch (range.toLowerCase()) {
      case 'close':
        // Both in center positions
        positions.player = 3;
        positions.opponent = 4;
        break;
      case 'medium':
        // One step out from center
        positions.player = 2;
        positions.opponent = 5;
        break;
      case 'far':
      default:
        // At opposite ends
        positions.player = 1;
        positions.opponent = 6;
        break;
    }

    return positions;
  }

  /**
   * Update icon position on the strip
   */
  _updateIconPosition(icon, position) {
    // Calculate left percentage based on position (1-6)
    // Each square is 16.666% wide, center of square is at 8.333% + (position-1)*16.666%
    const squareWidth = 100 / 6;
    const centerOffset = squareWidth / 2;
    const leftPercent = centerOffset + (position - 1) * squareWidth;
    
    icon.style.left = `${leftPercent}%`;
  }

  /**
   * Update health display with animation
   */
  animateHealthChange(isMyCharacter, newHealth) {
    const game = this.state.game;
    if (!game) return;
    const char = isMyCharacter ? game.myCharacter : game.opponentsCharacter;
    if (!char) return;
    const prefix = isMyCharacter ? 'my' : 'opponents';
    const card = this.shadowRoot.querySelector(`#${prefix}CharacterCard`);
    if (!card) return;

    const healthBar = card.querySelector('.progress-bar');
    const healthValue = card.querySelector('.health-value');

    if (healthBar && healthValue) {
      const percentage = Math.round((newHealth / char.startingHealth) * 100);

      healthBar.style.width = `${percentage}%`;
      healthBar.textContent = `${newHealth}`;

      // Update color
      healthBar.className = 'progress-bar';
      if (percentage > 66) {
        healthBar.classList.add('bg-success');
      } else if (percentage > 33) {
        healthBar.classList.add('bg-warning');
      } else {
        healthBar.classList.add('bg-danger');
      }

      // Animate number
      const startHealth = parseInt(healthValue.textContent.split('/')[0]);
      this._animateNumber(healthValue, startHealth, newHealth, char.startingHealth);
    }
  }

  /**
   * Helper: Animate number changing
   */
  _animateNumber(element, start, end, max) {
    const duration = 400;
    const steps = 20;
    const increment = (end - start) / steps;
    let current = start;
    let step = 0;

    const interval = setInterval(() => {
      step++;
      current += increment;

      if (step >= steps) {
        current = end;
        clearInterval(interval);
      }

      element.textContent = `${Math.round(current)} / ${max}`;
    }, duration / steps);
  }

  /**
   * Populate moves selection from state
   */
  renderMoveSelection() {
    const movesItemTemplate = document.getElementById('movesItemTemplate');
    const myMoves = this.elements.myMovesElement;

    if (!myMoves || !this.state.game) return;

    const theMoves = this.state.game.Moves;
    if (!theMoves || !theMoves.filteredMoves) return;

    myMoves.innerHTML = '';

    // Check if we should disable buttons during animation
    const shouldDisable = this.state.ui.animating;

    // Get unique tags from filtered moves
    const tags = [...new Set(theMoves.filteredMoves.map(move => move.tag))];
    const typeOrder = ['high', 'low', 'strong', 'defense', 'risky', 'extended', 'extended-defense'];

    tags.forEach(tag => {
      // Create group
      const groupDiv = document.createElement('div');
      groupDiv.className = 'move-group';

      const headerDiv = document.createElement('div');
      headerDiv.className = 'move-group-header';
      headerDiv.textContent = tag;
      groupDiv.appendChild(headerDiv);

      const cardsDiv = document.createElement('div');
      cardsDiv.className = 'move-group-cards';

      // Get and sort moves for this tag
      const tagMoves = theMoves.filteredMoves
        .filter(move => move.tag === tag)
        .sort((a, b) => typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type));

      tagMoves.forEach(move => {
        const moveItemClone = document.importNode(movesItemTemplate.content, true);
        const button = moveItemClone.querySelector('.move-card-button');
        const cardTag = moveItemClone.querySelector('.move-card-tag');
        const cardName = moveItemClone.querySelector('.move-card-name');
        const cardType = moveItemClone.querySelector('.move-card-type');
        const cardModifier = moveItemClone.querySelector('.move-card-modifier');

        // Calculate bonus
        const opponentBonuses = this.state.game.opponentsCharacter?.bonuses || [];
        const moveBonus = BonusCalculator.calculateBonus(move, opponentBonuses);

        cardTag.textContent = move.tag;
        cardName.textContent = move.name;
        cardType.textContent = move.type;

        // Build modifier display
        let modifierHTML = '';
        if (move.mod !== 0) {
          const sign = move.mod > 0 ? '+' : '';
          const badgeClass = move.mod > 0 ? 'bonus-badge' : 'restriction-badge';
          modifierHTML = `<span class="${badgeClass}">${sign}${move.mod} modifier</span>`;
        }
        if (moveBonus > 0) {
          modifierHTML += modifierHTML ? ' ' : '';
          modifierHTML += `<span class="bonus-badge">+${moveBonus} bonus</span>`;
        }
        cardModifier.innerHTML = modifierHTML;

        button.setAttribute('data-id', move.id);
        button.setAttribute('data-type', move.type);
        button.setAttribute('data-tag', move.tag);
        button.setAttribute('data-range', move.range);
        button.setAttribute('data-modifier', move.mod);

        // Disable button if animating
        button.disabled = shouldDisable;

        cardsDiv.appendChild(moveItemClone);
      });

      groupDiv.appendChild(cardsDiv);
      myMoves.appendChild(groupDiv);
    });
  }

  /**
   * Update disabled state of all move buttons based on animating flag
   */
  updateMoveButtonsState() {
    const buttons = this.shadowRoot.querySelectorAll('.move-card-button');
    const shouldDisable = this.state.ui.animating;

    buttons.forEach(button => {
      button.disabled = shouldDisable;
    });
  }
}

// ============================================================================
// PHASE 3: Animation Controller - Sequences animations
// ============================================================================

/**
 * AnimationController - Handles all animation sequences
 * Updates state when animations complete
 */
class AnimationController {
  constructor(shadowRoot, stateManager, uiRenderer) {
    this.shadowRoot = shadowRoot;
    this.state = stateManager;
    this.ui = uiRenderer;
  }

  /**
   * Show round animation sequence
   */
  async playRoundSequence(roundResults) {
    // Skip animation for round 0 (encounter start)
    if (this.state.game?.roundNumber === 0) {
      return;
    }

    this.state.ui.animating = true;
    this.ui.updateMoveButtonsState(); // Disable move buttons

    try {
      // Phase 1: Show face-down cards
      await this._showFaceDownCards();

      // Phase 2: Reveal cards
      await this._revealMoveCards(roundResults);

      // Phase 3: Show clash and effects
      await this._showClashEffects(roundResults);

      // Phase 4: Apply damage with animations
      await this._applyDamageAnimations(roundResults);

      // Phase 5: Update stats
      this._updateCharacterStats();

      // Phase 6: Cleanup
      await this._cleanupMoveCards();

    } finally {
      this.state.ui.animating = false;
      this.ui.updateMoveButtonsState(); // Re-enable move buttons
    }
  }

  async _showFaceDownCards() {
    this._showPlayerFaceDownCard();
    this._showOpponentFaceDownCard();
    await this._delay(500);
  }

  _showPlayerFaceDownCard() {
    const myWrapper = this.shadowRoot.querySelector('#myCharacterWrapper');
    if (!myWrapper) return;
    if (document.querySelector('.move-card.player')) return;

    // Clear current move section
    const currentMoveSection = myWrapper.querySelector('.current-move-section');
    if (currentMoveSection) {
      let contentArea = currentMoveSection.querySelector('.current-move-placeholder, .current-move');
      const placeholderHTML = '<div class="current-move-placeholder">Playing move...</div>';
      if (contentArea) {
        contentArea.outerHTML = placeholderHTML;
      } else {
        const tags = currentMoveSection.querySelectorAll('.sticky-tags');
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = placeholderHTML;
        if (tags.length > 0) {
          tags[tags.length - 1].insertAdjacentElement('afterend', tempDiv.firstElementChild);
        } else {
          currentMoveSection.appendChild(tempDiv.firstElementChild);
        }
      }
    }

    myWrapper.classList.add('move-submitted');

    const template = document.getElementById('moveCardTemplate');
    const playerClone = document.importNode(template.content, true);
    const playerCard = playerClone.querySelector('.move-card');
    playerCard.className = 'move-card player face-down';
    playerCard.querySelector('.card-back-label').textContent = 'You';
    playerCard.querySelector('.move-card-front').remove();

    // Apply character styling
    const character = this.state.game?.myCharacter;
    if (character) {
      const back = playerCard.querySelector('.move-card-back');
      const face = playerCard.querySelector('.card-face');
      if (character.icon && face) face.textContent = character.icon;
      if (character.color && back) back.style.backgroundColor = character.color;
      if (character.color) playerCard.style.setProperty('--character-color', character.color);
      // Store for consistency
      if (character.icon) playerCard.dataset.icon = character.icon;
      if (character.color) playerCard.dataset.color = character.color;
    }

    document.body.appendChild(playerCard);
  }

  _showOpponentFaceDownCard() {
    const opponentWrapper = this.shadowRoot.querySelector('#opponentsCharacterWrapper');
    if (!opponentWrapper) return;
    if (document.querySelector('.move-card.opponent')) return;

    // Clear current move section
    const currentMoveSection = opponentWrapper.querySelector('.current-move-section');
    if (currentMoveSection) {
      let contentArea = currentMoveSection.querySelector('.current-move-placeholder, .current-move');
      const placeholderHTML = '<div class="current-move-placeholder">Playing move...</div>';
      if (contentArea) {
        contentArea.outerHTML = placeholderHTML;
      } else {
        const tags = currentMoveSection.querySelectorAll('.sticky-tags');
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = placeholderHTML;
        if (tags.length > 0) {
          tags[tags.length - 1].insertAdjacentElement('afterend', tempDiv.firstElementChild);
        } else {
          currentMoveSection.appendChild(tempDiv.firstElementChild);
        }
      }
    }

    opponentWrapper.classList.add('move-submitted');

    const template = document.getElementById('moveCardTemplate');
    const opponentClone = document.importNode(template.content, true);
    const opponentCard = opponentClone.querySelector('.move-card');
    opponentCard.className = 'move-card opponent face-down';
    opponentCard.querySelector('.card-back-label').textContent =
      this.state.game?.opponentsCharacter?.playerName || 'Opponent';
    opponentCard.querySelector('.move-card-front').remove();

    const character = this.state.game?.opponentsCharacter;
    if (character) {
      const back = opponentCard.querySelector('.move-card-back');
      const face = opponentCard.querySelector('.card-face');
      if (character.icon && face) face.textContent = character.icon;
      if (character.color && back) back.style.backgroundColor = character.color;
      if (character.color) opponentCard.style.setProperty('--character-color', character.color);
      // Store for consistency
      if (character.icon) opponentCard.dataset.icon = character.icon;
      if (character.color) opponentCard.dataset.color = character.color;
    }

    document.body.appendChild(opponentCard);
  }

  async _revealMoveCards(roundResults) {
    // Replace face-down cards with full cards
    const playerCard = this._createFullMoveCard('player', roundResults);
    const opponentCard = this._createFullMoveCard('opponent', roundResults);

    const existingPlayer = document.querySelector('.move-card.player');
    const existingOpponent = document.querySelector('.move-card.opponent');

    if (existingPlayer) existingPlayer.replaceWith(playerCard);
    else document.body.appendChild(playerCard);

    if (existingOpponent) existingOpponent.replaceWith(opponentCard);
    else document.body.appendChild(opponentCard);

    await this._delay(500);

    // Trigger flip
    playerCard.classList.add('revealed');
    opponentCard.classList.add('revealed');

    // Update current move sections on character cards with round data from engine
    // Note: Show opponent's result on my card and my result on opponent's card
    if (this.state.game) {
      const { myRoundData, opponentsRoundData } = this.state.game;
      this._updateCurrentMoveSection('my', opponentsRoundData);
      this._updateCurrentMoveSection('opponents', myRoundData);
    }

    await this._delay(1200);
  }

  /**
   * Update the current move section on a character card
   * Shows the result of the clash (Hit/Miss/Block etc) not the move name
   */
  _updateCurrentMoveSection(prefix, roundData) {
    const wrapper = this.shadowRoot.querySelector(`#${prefix}CharacterWrapper`);
    if (!wrapper) return;

    const currentMoveSection = wrapper.querySelector('.current-move-section');
    if (!currentMoveSection) return;

    // Find or create the placeholder/content area (skip the sticky-tags)
    let contentArea = currentMoveSection.querySelector('.current-move-placeholder, .current-move');

    if (roundData?.result?.name) {
      const hit = roundData?.score ? true : false;

      const moveHTML = `
        <div class="current-move ${hit ? 'hit' : ''}">
          <div class="move-name">🎯 ${roundData.result.name}</div>
          ${roundData.range || roundData.totalScore ? `
            <div class="move-details">
              ${roundData.range ? `Range: ${roundData.range}` : ''}
              ${roundData.range && roundData.totalScore ? ' • ' : ''}
              ${roundData.totalScore ? `Score: ${roundData.totalScore}` : ''}
            </div>
          ` : ''}
        </div>
      `;
      
      if (contentArea) {
        contentArea.outerHTML = moveHTML;
      } else {
        const tags = currentMoveSection.querySelectorAll('.sticky-tags');
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = moveHTML;
        if (tags.length > 0) {
          tags[tags.length - 1].insertAdjacentElement('afterend', tempDiv.firstElementChild);
        } else {
          currentMoveSection.appendChild(tempDiv.firstElementChild);
        }
      }
    } else {
      const placeholderHTML = '<div class="current-move-placeholder">Waiting for action...</div>';
      if (contentArea) {
        contentArea.outerHTML = placeholderHTML;
      } else {
        const tags = currentMoveSection.querySelectorAll('.sticky-tags');
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = placeholderHTML;
        if (tags.length > 0) {
          tags[tags.length - 1].insertAdjacentElement('afterend', tempDiv.firstElementChild);
        } else {
          currentMoveSection.appendChild(tempDiv.firstElementChild);
        }
      }
    }
  }

  _createFullMoveCard(side, roundResults) {
    const isPlayer = side === 'player';
    const lastRound = this.state.roundHistory[this.state.roundHistory.length - 1];
    if (!lastRound) return null;

    const move = isPlayer ? lastRound.myMove : lastRound.opponentMove;
    const character = isPlayer ? this.state.game?.myCharacter : this.state.game?.opponentsCharacter;
    const damage = isPlayer ? roundResults.myDamage : roundResults.opponentDamage;
    const effects = isPlayer ? roundResults.myEffects : roundResults.opponentEffects;

    const template = document.getElementById('moveCardTemplate');
    const clone = document.importNode(template.content, true);
    const card = clone.querySelector('.move-card');

    card.className = `move-card ${side}`;

    const characterName = isPlayer ? 'You' : (character?.playerName || 'Opponent');

    // Populate card
    card.querySelector('.card-back-label').textContent = characterName;
    card.querySelector('.card-character-name').textContent = characterName;
    card.querySelector('.move-tag').textContent = move.tag || '';
    card.querySelector('.move-name').textContent = move.name;
    card.querySelector('.move-type').textContent = move.type || '';

    // Set data-type on front face for icon rendering
    const cardFront = card.querySelector('.move-card-front');
    if (cardFront) cardFront.setAttribute('data-type', move.type);

    // Apply character styling
    if (character) {
      const back = card.querySelector('.move-card-back');
      const face = card.querySelector('.card-face');
      if (character.icon && face) face.textContent = character.icon;
      if (character.color && back) back.style.backgroundColor = character.color;
      if (character.color) card.style.setProperty('--character-color', character.color);
      // Store for consistency
      if (character.icon) card.dataset.icon = character.icon;
      if (character.color) card.dataset.color = character.color;
    }

    // Store for later tagging
    card.dataset.damage = damage;
    card.dataset.effects = JSON.stringify(effects);

    return card;
  }

  async _showClashEffects(roundResults) {
    // Add effect tags to cards
    const playerCard = document.querySelector('.move-card.player');
    const opponentCard = document.querySelector('.move-card.opponent');

    if (playerCard) this._addEffectTags(playerCard, roundResults.myDamage, roundResults.myEffects, roundResults.myDamageBreakdown);
    if (opponentCard) this._addEffectTags(opponentCard, roundResults.opponentDamage, roundResults.opponentEffects, roundResults.opponentDamageBreakdown);

    await this._delay(800);
  }

  _addEffectTags(card, damage, effects, damageBreakdown) {
    const cardFront = card.querySelector('.move-card-front');
    if (!cardFront) return;

    const tags = [];

    // Damage tag with breakdown
    if (damage > 0) {
      const damageTag = document.createElement('div');
      damageTag.className = 'move-effect-tag damage-tag';
      let damageHTML = `<div>💥 ${damage} DMG</div>`;
      
      // Add breakdown if there are modifiers or bonuses
      if (damageBreakdown && (damageBreakdown.bonus !== 0 || damageBreakdown.modifier !== 0)) {
        const parts = [];
        if (damageBreakdown.base !== '' && damageBreakdown.base !== undefined) {
          parts.push(`${damageBreakdown.base} base`);
        }
        if (damageBreakdown.modifier) {
          parts.push(`${damageBreakdown.modifier > 0 ? '+' : ''}${damageBreakdown.modifier} move`);
        }
        if (damageBreakdown.bonus !== 0) {
          parts.push(`${damageBreakdown.bonus > 0 ? '+' : ''}${damageBreakdown.bonus} bonus`);
        }
        if (parts.length > 0) {
          damageHTML += `<div class="damage-breakdown">(${parts.join(', ')})</div>`;
        }
      }
      
      damageTag.innerHTML = damageHTML;
      tags.push(damageTag);
    } else if (damage === 0 && effects?.blocked) {
      const blockedTag = document.createElement('div');
      blockedTag.className = 'move-effect-tag blocked-tag';
      let blockedHTML = `<div>🛡️ BLOCKED</div>`;
      
      // Show what would have been the damage
      if (damageBreakdown && (damageBreakdown.bonus !== 0 || damageBreakdown.modifier !== 0)) {
        const parts = [];
        if (damageBreakdown.base !== '' && damageBreakdown.base !== undefined) {
          parts.push(`${damageBreakdown.base} base`);
        }
        if (damageBreakdown.modifier) {
          parts.push(`${damageBreakdown.modifier > 0 ? '+' : ''}${damageBreakdown.modifier} move`);
        }
        if (damageBreakdown.bonus !== 0) {
          parts.push(`${damageBreakdown.bonus > 0 ? '+' : ''}${damageBreakdown.bonus} bonus`);
        }
        if (parts.length > 0) {
          blockedHTML += `<div class="damage-breakdown">(${parts.join(', ')} = ${damage})</div>`;
        }
      }
      
      blockedTag.innerHTML = blockedHTML;
      tags.push(blockedTag);
    }

    // Self-damage
    if (effects?.selfDamage) {
      const selfTag = document.createElement('div');
      selfTag.className = 'move-effect-tag self-damage-tag';
      selfTag.innerHTML = `<div>⚠️ ${effects.selfDamage} SELF DMG</div>`;
      tags.push(selfTag);
    }

    // Healing
    if (effects?.heal) {
      const healTag = document.createElement('div');
      healTag.className = 'move-effect-tag heal-tag';
      healTag.innerHTML = `<div>💚 ${effects.heal} HEAL</div>`;
      tags.push(healTag);
    }

    // Append with stagger
    tags.forEach((tag, index) => {
      setTimeout(() => {
        cardFront.appendChild(tag);
        setTimeout(() => tag.classList.add('visible'), 10);
      }, index * 150);
    });
  }

  async _applyDamageAnimations(roundResults) {
    const myCard = this.shadowRoot.querySelector('#myCharacterCard');
    const opponentCard = this.shadowRoot.querySelector('#opponentsCharacterCard');

    // Add damage flash
    if (roundResults.opponentDamage > 0) {
      myCard?.classList.add('taking-damage');
    }
    if (roundResults.myDamage > 0) {
      opponentCard?.classList.add('taking-damage');
    }

    // Animate health changes simultaneously
    if (this.state.game) {
      this.ui.animateHealthChange(true, this.state.game.myCharacter.health);
      this.ui.animateHealthChange(false, this.state.game.opponentsCharacter.health);
    }

    await this._delay(600);

    myCard?.classList.remove('taking-damage');
    opponentCard?.classList.remove('taking-damage');
  }

  _updateCharacterStats() {
    // Re-render characters to pick up weapon/shield changes from engine
    this.ui.renderCharacter(true);
    this.ui.renderCharacter(false);

    // Update restrictions and bonuses
    this.ui.renderRestrictions(true);
    this.ui.renderRestrictions(false);
    this.ui.renderBonuses(true);
    this.ui.renderBonuses(false);

    // Update range strip with animation
    this.animateRangeChange();
  }

  /**
   * Animate range change on the strip
   */
  async animateRangeChange() {
    if (!this.ui.elements.rangeStrip || !this.state.game) return;

    const game = this.state.game;
    const newRange = game.range || 'far';
    const previousRange = this.state.previousRange;
    
    // Log range changes for debugging
    if (window.logging && newRange !== previousRange) {
      console.log(`Range changed: ${previousRange} → ${newRange}`);
    }

    // Update stored range
    this.state.previousRange = newRange;
    
    // Get current positions
    const playerIcon = this.ui.elements.rangeStrip.querySelector('.player-icon');
    const opponentIcon = this.ui.elements.rangeStrip.querySelector('.opponent-icon');
    
    const oldPlayerPos = parseInt(playerIcon?.dataset.position || '1');
    const oldOpponentPos = parseInt(opponentIcon?.dataset.position || '6');

    // Calculate new positions
    const newPositions = this.ui._calculateRangePositions(newRange);

    // Only animate if positions actually changed
    if (oldPlayerPos !== newPositions.player || oldOpponentPos !== newPositions.opponent) {
      if (window.logging) {
        console.log(`Animating range change: P${oldPlayerPos}→${newPositions.player}, O${oldOpponentPos}→${newPositions.opponent}`);
      }

      // Update positions with CSS transition
      if (playerIcon) {
        playerIcon.dataset.position = newPositions.player;
        this.ui._updateIconPosition(playerIcon, newPositions.player);
      }

      if (opponentIcon) {
        opponentIcon.dataset.position = newPositions.opponent;
        this.ui._updateIconPosition(opponentIcon, newPositions.opponent);
      }

      // Update occupied squares
      const squares = this.ui.elements.rangeStrip.querySelectorAll('.range-square');
      squares.forEach((square, index) => {
        const pos = index + 1;
        square.classList.toggle('occupied', pos === newPositions.player || pos === newPositions.opponent);
      });

      // Wait for animation to complete
      await this._delay(600);
    }
  }

  async _cleanupMoveCards() {
    const myWrapper = this.shadowRoot.querySelector('#myCharacterWrapper');
    const opponentWrapper = this.shadowRoot.querySelector('#opponentsCharacterWrapper');

    const cards = document.querySelectorAll('.move-card');
    const tags = document.querySelectorAll('.move-effect-tag');

    cards.forEach(card => card.classList.add('exit'));
    tags.forEach(tag => tag.classList.add('exit'));

    await this._delay(500);

    cards.forEach(card => card.remove());
    tags.forEach(tag => tag.remove());

    if (myWrapper) myWrapper.classList.remove('move-submitted');
    if (opponentWrapper) opponentWrapper.classList.remove('move-submitted');

    await this._delay(600);
  }

  _delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// ============================================================================
// Intro Form Manager - Single-page form flow
// ============================================================================

/**
 * IntroFormManager - Manages the single-page intro form
 */
class IntroFormManager {
  constructor(shadowRoot) {
    this.shadowRoot = shadowRoot;
    this.selectedGameMode = 'computer'; // Default to computer mode
  }

  setupEventListeners() {
    // Mode toggle buttons
    const modeComputer = this.shadowRoot.getElementById('modeComputer');
    const modeMultiplayer = this.shadowRoot.getElementById('modeMultiplayer');
    const inviteSection = this.shadowRoot.getElementById('inviteSection');
    const computerBtn = this.shadowRoot.getElementById('vsTheComputer');
    const multiplayerBtn = this.shadowRoot.getElementById('acceptInvite');

    modeComputer?.addEventListener('click', (e) => {
      e.preventDefault();
      this.selectedGameMode = 'computer';
      modeComputer.classList.add('active');
      modeMultiplayer?.classList.remove('active');
      if (inviteSection) inviteSection.style.display = 'none';
      if (computerBtn) computerBtn.style.display = 'block';
      if (multiplayerBtn) multiplayerBtn.style.display = 'none';
      this._validateForm();
    });

    modeMultiplayer?.addEventListener('click', (e) => {
      e.preventDefault();
      this.selectedGameMode = 'multiplayer';
      modeMultiplayer.classList.add('active');
      modeComputer?.classList.remove('active');
      if (inviteSection) inviteSection.style.display = 'block';
      if (computerBtn) computerBtn.style.display = 'none';
      if (multiplayerBtn) multiplayerBtn.style.display = 'block';
      this._validateForm();
    });

    // Form validation on input changes
    this.shadowRoot.getElementById('playerName')?.addEventListener('input', () => {
      this._validateForm();
    });

    // Character selection
    this.shadowRoot.addEventListener('character-selected', () => {
      this._validateForm();
    });
  }

  _validateForm() {
    const playerName = this.shadowRoot.getElementById('playerName')?.value.trim();
    const characterSlug = this.shadowRoot.getElementById('myCharacter')?.value;
    const computerBtn = this.shadowRoot.getElementById('vsTheComputer');
    const multiplayerBtn = this.shadowRoot.getElementById('acceptInvite');

    const isValid = playerName && characterSlug;

    if (computerBtn) {
      computerBtn.disabled = !isValid;
    }
    if (multiplayerBtn) {
      multiplayerBtn.disabled = !isValid;
    }
  }

  populateInviteCode(invite) {
    const inviteIdInput = this.shadowRoot.getElementById('inviteId');
    if (inviteIdInput && invite) {
      inviteIdInput.value = invite;
      inviteIdInput.dataset.gameId = invite;
      inviteIdInput.dataset.invite = true;
      
      // Switch to multiplayer mode
      const modeMultiplayer = this.shadowRoot.getElementById('modeMultiplayer');
      modeMultiplayer?.click();
    }

    const shareLink = this.shadowRoot.getElementById('shareLink');
    if (shareLink && invite) {
      shareLink.value = window.location.href.split('?')[0] + '?invite=' + invite;
    }
  }

  populateGameId(gameId) {
    const gameIdInput = this.shadowRoot.getElementById('gameId');
    if (gameIdInput && gameId) {
      gameIdInput.value = gameId;
      gameIdInput.dataset.gameId = gameId;
    }

    const shareInviteCode = this.shadowRoot.getElementById('shareInviteCode');
    if (shareInviteCode && gameId) {
      shareInviteCode.innerHTML = gameId;
    }

    const shareLink = this.shadowRoot.getElementById('shareLink');
    if (shareLink && gameId) {
      shareLink.value = window.location.href.split('?')[0] + '?invite=' + gameId;
      shareLink.dataset.gameId = gameId;
    }
  }
}

// ============================================================================
// Modal Manager - Centralized modal control
// ============================================================================

/**
 * ModalManager - Manages all modal interactions
 */
class ModalManager {
  constructor(shadowRoot) {
    this.shadowRoot = shadowRoot;
    this.modals = {};
  }

  initialize() {
    this.modals.info = new Modal(this.shadowRoot.getElementById('infoModal'), { keyboard: false });
    this.modals.newGame = new Modal(this.shadowRoot.getElementById('newGameModal'), { keyboard: false });
    this.modals.log = new Modal(this.shadowRoot.getElementById('logModal'), { keyboard: false });
    this.movesDrawer = this.shadowRoot.getElementById('movesDrawer');

    this._setupDrawer();
    this._setupLogModal();
  }

  _setupDrawer() {
    if (!this.movesDrawer) return;

    const closeBtn = this.movesDrawer.querySelector('.moves-drawer-close');
    const overlay = this.movesDrawer.querySelector('.moves-drawer-overlay');

    closeBtn?.addEventListener('click', () => this.closeDrawer());
    overlay?.addEventListener('click', () => this.closeDrawer());

    // Setup scroll indicator
    const body = this.movesDrawer.querySelector('.moves-drawer-body');
    const indicator = this.movesDrawer.querySelector('.moves-drawer-scroll-indicator');

    body?.addEventListener('scroll', () => {
      if (body.scrollHeight > body.clientHeight) {
        const isNearBottom = body.scrollTop + body.clientHeight >= body.scrollHeight - 50;
        indicator?.classList.toggle('visible', !isNearBottom);
      }
    });
  }

  _setupLogModal() {
    const logModalElement = this.shadowRoot.getElementById('logModal');
    logModalElement?.addEventListener('shown.bs.modal', () => {
      const gameLogBody = this.shadowRoot.getElementById('gameLogBody');
      if (gameLogBody) {
        gameLogBody.scrollTop = gameLogBody.scrollHeight;
      }
    });
  }

  showInfo(content, className) {
    const modalContent = this.modals.info._element.querySelector('#infoList');
    const infoItemTemplate = document.getElementById('infoItemTemplate');
    const infoItem = document.importNode(infoItemTemplate.content, true);
    const infoItemContent = infoItem.querySelector('.info-item');

    infoItemContent.innerHTML = content;
    infoItemContent.classList.add(className);

    modalContent.innerHTML = '';
    modalContent.appendChild(infoItem);

    this.modals.info.show();
  }

  showWaitingModal() {
    this.modals.newGame.show();
  }

  hideWaitingModal() {
    this.modals.newGame.hide();
  }

  showLog() {
    this.modals.log.show();
  }

  openDrawer() {
    this.movesDrawer?.classList.add('open');
    document.body.style.overflow = 'hidden';

    // Check if scroll indicator should be visible
    const body = this.movesDrawer?.querySelector('.moves-drawer-body');
    const indicator = this.movesDrawer?.querySelector('.moves-drawer-scroll-indicator');
    if (body && body.scrollHeight > body.clientHeight) {
      indicator?.classList.add('visible');
    }
  }

  closeDrawer() {
    this.movesDrawer?.classList.remove('open');
    document.body.style.overflow = '';
  }
}

// ============================================================================
// Combat Log Renderer - Manages round history display
// ============================================================================

/**
 * CombatLogRenderer - Renders round history to combat log
 */
class CombatLogRenderer {
  constructor(shadowRoot, stateManager) {
    this.shadowRoot = shadowRoot;
    this.state = stateManager;
    this.roundsElement = shadowRoot.getElementById('Rounds');
  }

  /**
   * Add round to combat log
   */
  addRoundToLog(roundData) {
    if (!this.roundsElement) return;

    const roundElement = this._createRoundElement();

    if (roundData.number === 0) {
      // Encounter start
      this._renderEncounterStart(roundElement);
    } else {
      // Combat round
      this._renderCombatRound(roundElement, roundData);
    }

    this.roundsElement.appendChild(roundElement);
  }

  _createRoundElement() {
    const roundTemplate = document.getElementById('roundTemplate');
    const roundClone = document.importNode(roundTemplate.content, true);
    return roundClone.querySelector('.round-summary');
  }

  _renderEncounterStart(roundElement) {
    const summaryItemTemplate = document.getElementById('summaryItemTemplate');
    const summaryItem = document.importNode(summaryItemTemplate.content, true);
    const summaryItemContent = summaryItem.querySelector('.summary-item');

    const opponentName = this.state.game?.opponentsCharacter?.name || 'Your opponent';
    const opponentSlug = this.state.game?.opponentsCharacter?.slug || 'human-fighter';

    const location = randomChoice(flavorTextData.locations);
    const introduction = randomChoice(
      flavorTextData.introductions[opponentSlug] ||
      flavorTextData.introductions['human-fighter']
    );
    const ready = randomChoice(flavorTextData.ready);

    summaryItemContent.innerHTML = `
      <em style="opacity: 0.8;">${location}</em><br><br>
      <em style="color: var(--bs-warning);">${introduction}</em><br><br>
      <strong>${opponentName} challenges you to single combat!</strong><br><br>
      <em style="opacity: 0.9;">${ready}</em>
    `;

    roundElement.appendChild(summaryItem);
  }

  _renderCombatRound(roundElement, roundData) {
    // Move descriptions
    this._addMoveDescriptions(roundElement, roundData);

    // Player summary
    this._addCharacterSummary(roundElement, roundData, true);

    // Opponent summary
    this._addCharacterSummary(roundElement, roundData, false);

    // Special effects
    this._addSpecialEffects(roundElement, roundData);
  }

  _addMoveDescriptions(roundElement, roundData) {
    const summaryItemTemplate = document.getElementById('summaryItemTemplate');
    const summaryItem = document.importNode(summaryItemTemplate.content, true);
    const summaryItemContent = summaryItem.querySelector('.summary-item');

    const playerMove = roundData.myMove;
    const opponentMove = roundData.opponentMove;
    const results = roundData.results;

    const playerHit = results.myDamage > 0;
    const opponentHit = results.opponentDamage > 0;
    const hasRestrictions = results.myEffects.restrictions.length > 0 ||
                           results.opponentEffects.restrictions.length > 0;

    const hitPattern = `${playerHit ? 'playerHits' : 'playerMiss'}${opponentHit ? 'OpponentHits' : 'OpponentMiss'}`
      .replace('playerMissOpponentMiss', 'bothMiss')
      .replace('playerHitsOpponentHits', 'bothHit');
    const flavorCategory = hasRestrictions ? `${hitPattern}Restricted` : hitPattern;

    const roundFlavor = randomChoice(flavorTextData.roundResults[flavorCategory]);

    const playerMoveText = playerMove.tag
      ? `<span style="opacity: 0.7;">${playerMove.tag.toLowerCase()}:</span> ${playerMove.name.toLowerCase()}`
      : playerMove.name.toLowerCase();

    const opponentMoveText = opponentMove.tag
      ? `<span style="opacity: 0.7;">${opponentMove.tag.toLowerCase()}:</span> ${opponentMove.name.toLowerCase()}`
      : opponentMove.name.toLowerCase();

    let html = '';
    if (roundFlavor) {
      html += `<em>${roundFlavor}</em><br><br>`;
    }

    html += `<em>You ${playerMoveText}, they ${opponentMoveText}.</em>`;

    summaryItemContent.innerHTML = html;
    summaryItemContent.classList.add('move-descriptions');
    roundElement.appendChild(summaryItem);
  }

  _addCharacterSummary(roundElement, roundData, isMyCharacter) {
    const summaryItemTemplate = document.getElementById('summaryItemTemplate');
    const summaryItem = document.importNode(summaryItemTemplate.content, true);
    const summaryItemContent = summaryItem.querySelector('.summary-item');

    const char = isMyCharacter ? this.state.game?.myCharacter : this.state.game?.opponentsCharacter;
    const move = isMyCharacter ? roundData.myMove : roundData.opponentMove;
    const damage = isMyCharacter ? roundData.results.myDamage : roundData.results.opponentDamage;
    const damageTaken = isMyCharacter ? roundData.results.opponentDamage : roundData.results.myDamage;
    const effects = isMyCharacter ? roundData.results.myEffects : roundData.results.opponentEffects;
    const opponentEffects = isMyCharacter ? roundData.results.opponentEffects : roundData.results.myEffects;

    const characterName = isMyCharacter ? 'You' : (char.playerName || char.data?.name || 'Opponent');
    const prefix = isMyCharacter ? 'my' : 'opponents';

    summaryItemContent.classList.add(`${prefix}-summary-item`);

    let html = `<strong>➤ ${characterName}:</strong> ${move.name}`;

    if (damageTaken > 0) {
      html += `<br>💔 Took ${damageTaken} damage from opponent`;
    } else if (damageTaken === 0 && opponentEffects.blocked) {
      html += `<br>🛡️ Attack from opponent blocked`;
    }

    if (damage > 0) {
      html += `<br>💥 Dealt ${damage} damage to opponent`;
    } else if (damage === 0 && effects.blocked) {
      html += `<br>🛡️ Your attack was blocked`;
    }

    if (effects.nextRoundBonus?.length > 0) {
      const bonusText = effects.nextRoundBonus.map(bonusObj => {
        const [type, value] = Object.entries(bonusObj)[0];
        return `${value > 0 ? '+' : ''}${value} to ${type}`;
      }).join(', ');
      html += `<br>⭐ Next round: ${bonusText}`;
    }

    if (effects.restrictions?.length > 0) {
      html += `<br>⚠️ Restrictions: ${effects.restrictions.join(', ')}`;
    }

    if (effects.allowOnly?.length > 0) {
      html += `<br>✓ Allowed: ${effects.allowOnly.join(', ')}`;
    }

    summaryItemContent.innerHTML = html;
    roundElement.appendChild(summaryItem);
  }

  _addSpecialEffects(roundElement, roundData) {
    const effects = [];
    const myEffects = roundData.results.myEffects;
    const opponentEffects = roundData.results.opponentEffects;

    if (myEffects.weaponDislodged) {
      effects.push('⚔️ Opponent\'s weapon was dislodged!');
    }
    if (opponentEffects.weaponDislodged) {
      effects.push('⚔️ Your weapon was dislodged!');
    }

    if (myEffects.shieldDestroyed) {
      effects.push('🛡️ Their shield splinters apart!');
    }
    if (opponentEffects.shieldDestroyed) {
      effects.push('🛡️ Your shield shatters under the blow!');
    }

    if (opponentEffects.heal) {
      effects.push(`💚 Opponent regenerated ${opponentEffects.heal} HP!`);
    }
    if (myEffects.heal) {
      effects.push(`💚 You regenerated ${myEffects.heal} HP!`);
    }

    if (effects.length > 0) {
      const summaryItemTemplate = document.getElementById('summaryItemTemplate');
      const summaryItem = document.importNode(summaryItemTemplate.content, true);
      const summaryItemContent = summaryItem.querySelector('.summary-item');
      summaryItemContent.innerHTML = effects.join('<br>');
      summaryItemContent.classList.add('special-effects');
      roundElement.appendChild(summaryItem);
    }
  }
}

// ============================================================================
// PHASE 4: Network Adapter - Syncs with engine
// ============================================================================

/**
 * NetworkAdapter - Handles communication with game engine
 * Translates between engine events and state updates
 */
class NetworkAdapter {
  constructor(stateManager, component) {
    this.state = stateManager;
    this.component = component;
    this.eventHandlers = new Map();

    // Event queue to process events sequentially
    this.eventQueue = [];
    this.isProcessingQueue = false;
  }

  /**
   * Queue an event for sequential processing
   * @private
   */
  _queueEvent(eventName, handler, event) {
    this.eventQueue.push({ eventName, handler, event });
    this._processQueue();
  }

  /**
   * Process events from the queue one at a time
   * Ensures animations complete before advancing to next event
   * @private
   */
  async _processQueue() {
    if (this.isProcessingQueue || this.eventQueue.length === 0) {
      return;
    }

    this.isProcessingQueue = true;

    while (this.eventQueue.length > 0) {
      const { eventName, handler, event } = this.eventQueue.shift();

      try {
        await handler(event);
      } catch (error) {
        console.error(`Error processing ${eventName}:`, error);
      }
    }

    this.isProcessingQueue = false;
  }

  /**
   * Initialize engine game instance
   */
  async initializeGame(gameId, characterSlug, isComputerGame) {
    const game = new Game(gameId, characterSlug);
    await game.initialize();

    this.state.game = game;
    this.state.gameId = gameId;
    this.state.gameMode = isComputerGame ? 'computer' : 'multiplayer';

    // Engine already has character data - no sync needed

    return game;
  }

  /**
   * Create and attach transport
   */
  async attachTransport(isComputerGame) {
    const game = this.state.game;

    if (isComputerGame) {
      const { ComputerTransport } = await import('swordfight-engine/transports/ComputerTransport');
      game.transport = new ComputerTransport(game);
    } else {
      game.transport = new DurableObjectTransport(game, {
        serverUrl: CONFIG.MULTIPLAYER_SERVER_URL
      });
    }
  }

  /**
   * Connect to transport
   */
  async connect() {
    const game = this.state.game;
    await game.connect(game.transport);
  }

  /**
   * Register event handlers
   */
  registerEventHandlers() {
    this.on('start', () => {
      if (window.logging) console.log('Game started');
    });

    this.on('name', async (e) => {
      if (window.logging) console.log('Opponent character received');

      // Set player name from event (engine handles character data)
      if (e.detail?.name && this.state.game?.opponentsCharacter) {
        this.state.game.opponentsCharacter.playerName = e.detail.name;
      }
    });

    this.on('setup', () => {
      if (window.logging) console.log('Game setup complete');

      // Don't render if already rendered (setup can fire multiple times)
      if (this.component.uiRenderer.elements.game.style.display === 'block') {
        return;
      }

      // Initialize range to far (starting distance)
      if (this.state.game) {
        this.state.game.range = this.state.game.range || 'far';
        this.state.previousRange = 'far';
      }

      // Hide waiting modal and show game view
      this.component.modalManager.hideWaitingModal();
      this.component.uiRenderer.renderGameView();

      // Add encounter start to combat log
      this.component.combatLogRenderer.addRoundToLog({
        number: 0,
        myMove: null,
        opponentMove: null,
        results: null
      });
    });

    this.on('myMove', () => {
      // Show player's face-down card immediately when move is submitted
      this.component.animationController._showPlayerFaceDownCard();
    });

    this.on('opponentsMove', () => {
      // Show opponent's face-down card when their move is received
      this.component.animationController._showOpponentFaceDownCard();
    });

    this.on('round', (e) => {
      // Queue round event to ensure sequential processing with animations
      this._queueEvent('round', async (event) => {
        // Extract minimal round data from engine event
        const { myRoundData, opponentsRoundData } = event.detail;

        // Debug: Log event detail to see what's available
        if (window.logging) {
          console.log('Round event detail:', event.detail);
          console.log('Range from myRoundData:', myRoundData?.range);
          console.log('Range from opponentsRoundData:', opponentsRoundData?.range);
        }

        // Update game range from round data (use myRoundData as it has the current range)
        if (myRoundData?.range && this.state.game) {
          if (window.logging && this.state.game.range !== myRoundData.range) {
            console.log(`Range change detected: ${this.state.game.range} → ${myRoundData.range}`);
          }
          this.state.game.range = myRoundData.range;
        }

        // Convert to our minimal format
        const results = {
          myDamage: myRoundData.totalScore || 0,
          opponentDamage: opponentsRoundData.totalScore || 0,
          myDamageBreakdown: {
            total: myRoundData.totalScore || 0,
            base: myRoundData.score,
            modifier: myRoundData.moveModifier || 0,
            bonus: myRoundData.bonus || 0
          },
          opponentDamageBreakdown: {
            total: opponentsRoundData.totalScore || 0,
            base: opponentsRoundData.score,
            modifier: opponentsRoundData.moveModifier || 0,
            bonus: opponentsRoundData.bonus || 0
          },
          myEffects: {
            heal: opponentsRoundData.result?.heal,
            selfDamage: opponentsRoundData.result?.selfDamage,
            restrictions: opponentsRoundData.result?.restrict || [],
            allowOnly: opponentsRoundData.result?.allowOnly || [],
            nextRoundBonus: opponentsRoundData.nextRoundBonus || [],
            weaponDislodged: opponentsRoundData.result?.weaponDislodged,
            shieldDestroyed: opponentsRoundData.result?.shieldDestroyed,
            blocked: opponentsRoundData.totalScore === 0 && opponentsRoundData.score !== ''
          },
          opponentEffects: {
            heal: myRoundData.result?.heal,
            selfDamage: myRoundData.result?.selfDamage,
            restrictions: myRoundData.result?.restrict || [],
            allowOnly: myRoundData.result?.allowOnly || [],
            nextRoundBonus: myRoundData.nextRoundBonus || [],
            weaponDislodged: myRoundData.result?.weaponDislodged,
            shieldDestroyed: myRoundData.result?.shieldDestroyed,
            blocked: myRoundData.totalScore === 0 && myRoundData.score !== ''
          }
        };

        // Create round data for history
        const roundData = {
          number: this.state.game.roundNumber,
          myMove: myRoundData.myMove,
          opponentMove: opponentsRoundData.myMove,
          results
        };

        // Trigger round complete callback (this awaits the animation)
        await this.state.triggerRoundComplete(roundData);
      }, e);
    });

    this.on('victory', (e) => {
      // Queue victory event to ensure it processes after round animation completes
      this._queueEvent('victory', async () => {
        if (window.logging) console.log('Victory!');
        this.component._handleGameOver();
      }, e);
    });

    this.on('defeat', (e) => {
      // Queue defeat event to ensure it processes after round animation completes
      this._queueEvent('defeat', async () => {
        if (window.logging) console.log('Defeat!');
        this.component._handleGameOver();
      }, e);
    });

    this.on('roomFull', () => {
      if (window.logging) console.log('Room full');
      this.component.modalManager.showInfo(
        'This sword fight has already started!',
        'bg-danger'
      );
    });
  }

  /**
   * Register an event handler
   */
  on(eventName, handler) {
    if (!this.eventHandlers.has(eventName)) {
      const boundHandler = handler.bind(this);
      this.eventHandlers.set(eventName, boundHandler);
      document.addEventListener(eventName, boundHandler);
    }
  }

  /**
   * Unregister all event handlers
   */
  cleanup() {
    this.eventHandlers.forEach((handler, eventName) => {
      document.removeEventListener(eventName, handler);
    });
    this.eventHandlers.clear();
  }

  /**
   * Submit a move
   */
  submitMove(moveId) {
    const moveEvent = new CustomEvent('inputMove', {
      detail: { move: moveId }
    });
    document.dispatchEvent(moveEvent);
  }
}

// ============================================================================
// Main SwordFight Component (Refactored)
// ============================================================================

export class SwordFight extends HTMLElement {
  constructor() {
    super();

    // Initialize state manager
    this.stateManager = new StateManager();

    // Parse URL parameters
    this._parseUrlParameters();

    // Configure logging
    window.logging = window.location.hostname !== CONFIG.PRODUCTION_HOSTNAME;
    if (window.logging) {
      console.log(`🎮 Running with ${ENGINE_VERSION.toUpperCase()} engine (Refactored)`);
    }
  }

  _parseUrlParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const gameId = urlParams.get('gameId');
    const invite = urlParams.get('invite');

    if (invite) {
      this.stateManager.ui.invite = invite;
    }

    if (gameId) {
      this.stateManager.gameId = gameId;
    }

    // Don't generate a gameId here - let the intro form handle that
  }

  _generateGameId() {
    return Math.random().toString(36).slice(-CONFIG.GAME_ID_LENGTH).toUpperCase();
  }

  async connectedCallback() {
    // Set up shadow DOM
    this._setupShadowDOM();

    // Load styles
    await this._loadStyles();

    // Load flavor text
    await loadFlavorText();

    // Initialize Bootstrap modals
    this._initializeModals();

    // Randomize terrain
    this._randomizeTerrain();

    // Initialize managers (PHASE 2)
    this.uiRenderer = new UIRenderer(this.shadowRoot, this.stateManager);
    this.animationController = new AnimationController(this.shadowRoot, this.stateManager, this.uiRenderer);
    this.combatLogRenderer = new CombatLogRenderer(this.shadowRoot, this.stateManager);
    this.modalManager = new ModalManager(this.shadowRoot);
    this.introFormManager = new IntroFormManager(this.shadowRoot);
    this.networkAdapter = new NetworkAdapter(this.stateManager, this);

    // Initialize modals through manager
    this.modalManager.initialize();

    // Register network event handlers
    this.networkAdapter.registerEventHandlers();

    // Set up UI event listeners
    this._setupEventListeners();

    // Initialize game
    await this.init();
  }

  disconnectedCallback() {
    this.networkAdapter?.cleanup();
  }

  _setupShadowDOM() {
    const shadowRoot = this.attachShadow({ mode: 'open' });
    const template = this.querySelector('#swordFightTemplate');
    const clone = document.importNode(template.content, true);
    shadowRoot.appendChild(clone);
  }

  async _loadStyles() {
    const response = await fetch('/assets/styles/styles.css');
    const styles = await response.text();
    const styleElement = document.createElement('style');
    styleElement.textContent = styles;
    this.shadowRoot.appendChild(styleElement);
  }

  _initializeModals() {
    // Modals initialized through ModalManager now
  }

  _randomizeTerrain() {
    const gameSection = this.shadowRoot.querySelector('.game');
    if (!gameSection) return;

    // Create character backdrop board
    const backdropBoard = document.createElement('div');
    backdropBoard.className = 'character-backdrop-board';
    gameSection.insertBefore(backdropBoard, gameSection.firstChild);

    const terrainEmoji = ['🌲', '🌲', '🌳', '🌳', '🪨', '🪵', '⛰️', '🏔️', '🌱', '🌾'];
    const terrainCount = 12;
    const placedPositions = [];

    for (let i = 0; i < terrainCount; i++) {
      const terrain = document.createElement('div');
      terrain.className = 'terrain-piece';
      const emoji = terrainEmoji[Math.floor(Math.random() * terrainEmoji.length)];
      terrain.textContent = emoji;

      let scale = 3;
      if (emoji === '🌲' || emoji === '🌳') scale = 5;
      else if (emoji === '⛰️' || emoji === '🏔️') scale = 10;

      let x, y, attempts = 0, validPosition = false;
      const minDistance = scale * 8;

      while (!validPosition && attempts < 50) {
        x = 5 + Math.random() * 90;
        y = 5 + Math.random() * 90;

        validPosition = placedPositions.every(pos => {
          const dx = x - pos.x;
          const dy = y - pos.y;
          return Math.sqrt(dx * dx + dy * dy) >= minDistance;
        });

        attempts++;
      }

      if (validPosition) {
        terrain.style.left = `${x}%`;
        terrain.style.top = `${y}%`;
        terrain.style.transform = `translateZ(20px)`;
        terrain.style.fontSize = `${scale * 100}%`;
        placedPositions.push({ x, y, scale });
        backdropBoard.appendChild(terrain);
      }
    }
  }

  // ============================================================================
  // PHASE 3: Simplified Game Flow
  // ============================================================================

  async init() {
    const savedCharacter = localStorage.getItem(CONFIG.STORAGE_KEYS.CHARACTER);
    const gameInProgress = localStorage.getItem(CONFIG.STORAGE_KEYS.GAME_IN_PROGRESS);

    // Check for saved game restoration
    if (this.stateManager.gameId && savedCharacter) {
      // We have a game ID and character - create game
      await this.createGame(this.stateManager.gameId, savedCharacter);

      // If there was a game in progress, try to restore it
      if (gameInProgress && this.stateManager.gameMode === 'computer') {
        await this._restoreSavedGame();
      }
    } else {
      // Show intro
      await this.showIntro();
    }
  }

  /**
   * Restore saved game state from localStorage
   */
  async _restoreSavedGame() {
    try {
      const savedState = localStorage.getItem(this.stateManager.gameId);
      if (!savedState) return;

      const state = JSON.parse(savedState);

      // Restore character health and equipment directly in engine
      if (state.myCharacter && this.stateManager.game) {
        this.stateManager.game.myCharacter.health = state.myCharacter.health;
        this.stateManager.game.myCharacter.weapon = state.myCharacter.weapon;
        this.stateManager.game.myCharacter.shield = state.myCharacter.shield;
      }

      if (state.opponentCharacter && this.stateManager.game) {
        this.stateManager.game.opponentsCharacter.health = state.opponentCharacter.health;
        this.stateManager.game.opponentsCharacter.weapon = state.opponentCharacter.weapon;
        this.stateManager.game.opponentsCharacter.shield = state.opponentCharacter.shield;
      }

      // Restore round history
      if (state.roundHistory) {
        this.stateManager.roundHistory = state.roundHistory;

        // Rebuild combat log
        state.roundHistory.forEach(round => {
          this.combatLogRenderer.addRoundToLog(round);
        });
      }

      // Re-render view with restored state
      this.uiRenderer.renderGameView();

      if (window.logging) {
        console.log('Restored saved game');
      }
    } catch (error) {
      console.error('Failed to restore saved game:', error);
      // Clear corrupted save
      localStorage.removeItem(this.stateManager.gameId);
      localStorage.removeItem(CONFIG.STORAGE_KEYS.GAME_IN_PROGRESS);
    }
  }

  async createGame(gameId, characterSlug) {
    const isComputerGame = gameId === 'computer';

    // Hide intro
    this.shadowRoot.getElementById('Intro').style.display = 'none';

    // Show waiting modal
    this.modalManager.showWaitingModal();

    // Hide invite code section for computer games
    const modalBody = this.shadowRoot.querySelector('#newGameModalBody .card-body');
    if (modalBody) {
      modalBody.style.display = isComputerGame ? 'none' : 'block';
    }

    // Initialize game (PHASE 4: Network Adapter)
    await this.networkAdapter.initializeGame(
      isComputerGame ? this._generateGameId() : gameId,
      characterSlug,
      isComputerGame
    );

    // Attach transport
    await this.networkAdapter.attachTransport(isComputerGame);

    // Connect (triggers character exchange and setup event)
    await this.networkAdapter.connect();

    // Setup event will handle showing the game view
  }

  async _waitForSetup() {
    return new Promise(resolve => {
      const check = () => {
        // Wait for both characters to be loaded in the engine
        if (this.stateManager.game?.myCharacter && this.stateManager.game?.opponentsCharacter) {
          resolve();
        } else {
          setTimeout(check, 100);
        }
      };
      check();
    });
  }

  async showIntro() {
    this.shadowRoot.getElementById('Game').style.display = 'none';
    this.shadowRoot.getElementById('Intro').style.display = 'block';

    // Populate character selector
    await this._populateCharacterSelector();

    // Restore saved values
    const playerName = localStorage.getItem(CONFIG.STORAGE_KEYS.PLAYER_NAME);
    if (playerName) {
      this.shadowRoot.getElementById('playerName').value = playerName;
    }

    // Setup intro form flow
    this.introFormManager.setupEventListeners();

    // Handle invite code if present
    if (this.stateManager.ui.invite) {
      this.introFormManager.populateInviteCode(this.stateManager.ui.invite);
      this.shadowRoot.getElementById('selectJoinGame')?.click();
    } else if (this.stateManager.gameId) {
      this.introFormManager.populateGameId(this.stateManager.gameId);
    }
  }

  async _populateCharacterSelector() {
    const characterSelector = this.shadowRoot.getElementById('characterSelector');
    const characterInput = this.shadowRoot.getElementById('myCharacter');
    if (!characterSelector || !characterInput) return;

    const availableCharacters = await Game.getAvailableCharacters();
    characterSelector.innerHTML = '';

    const cardTemplate = document.getElementById('characterCardSelectorTemplate');
    if (!cardTemplate) return;

    for (const characterSlug of availableCharacters) {
      const characterData = await Game.getCharacter(characterSlug);
      const cardClone = document.importNode(cardTemplate.content, true);
      const cardItem = cardClone.querySelector('.character-card-item');
      const characterCard = cardClone.querySelector('character-card');

      cardItem.dataset.characterSlug = characterSlug;

      // Handle weapon - can be string, array, or false
      let hasWeapon = false;
      let weaponName = '';
      if (Array.isArray(characterData.weapons) && characterData.weapons.length > 0) {
        hasWeapon = true;
        weaponName = characterData.weapons.map(w => w.name || w).join(', ');
      } else if (typeof characterData.weapon === 'string') {
        hasWeapon = true;
        weaponName = characterData.weapon;
      } else if (characterData.weapon && characterData.weapon !== false) {
        hasWeapon = true;
        weaponName = characterData.weapon.name || 'Armed';
      }

      // Handle shield - can be string or false
      const hasShield = characterData.shield && characterData.shield !== false;
      const shieldName = hasShield ? (typeof characterData.shield === 'string' ? characterData.shield : 'Shield') : '';

      characterCard.setAttribute('character-name', characterData.name || characterSlug);
      characterCard.setAttribute('character-slug', characterSlug);
      if (characterData.icon) characterCard.setAttribute('character-icon', characterData.icon);
      characterCard.setAttribute('health', characterData.health || '100');
      characterCard.setAttribute('starting-health', characterData.health || '100');
      characterCard.setAttribute('weapon', hasWeapon);
      characterCard.setAttribute('shield', hasShield);
      if (weaponName) characterCard.setAttribute('weapon-name', weaponName);
      if (shieldName) characterCard.setAttribute('shield-name', shieldName);
      characterCard.setAttribute('description', characterData.description || '');

      if (characterData.color) {
        characterCard.style.setProperty('--character-color', characterData.color);
      }

      cardItem.addEventListener('click', () => {
        characterSelector.querySelectorAll('.character-card-item').forEach(c => {
          c.classList.remove('selected');
        });
        cardItem.classList.add('selected');
        characterInput.value = characterSlug;
        localStorage.setItem(CONFIG.STORAGE_KEYS.CHARACTER, characterSlug);

        this.shadowRoot.dispatchEvent(new CustomEvent('character-selected', {
          detail: { characterSlug },
          bubbles: true,
          composed: true
        }));
      });

      characterSelector.appendChild(cardClone);
    }

    // Restore saved selection
    const savedCharacter = localStorage.getItem(CONFIG.STORAGE_KEYS.CHARACTER);
    if (savedCharacter && availableCharacters.includes(savedCharacter)) {
      characterInput.value = savedCharacter;
      const savedCard = characterSelector.querySelector(`[data-character-slug="${savedCharacter}"]`);
      savedCard?.classList.add('selected');
    }
  }

  // ============================================================================
  // Event Listeners
  // ============================================================================

  _setupEventListeners() {
    // Form submission
    this.shadowRoot.getElementById('gameForm')?.addEventListener('submit', (e) => {
      e.preventDefault();

      const playerName = this.shadowRoot.getElementById('playerName').value;
      const characterSlug = this.shadowRoot.getElementById('myCharacter').value;

      localStorage.setItem(CONFIG.STORAGE_KEYS.PLAYER_NAME, playerName);
      localStorage.setItem(CONFIG.STORAGE_KEYS.CHARACTER, characterSlug);

      const gameId = e.submitter.id === 'vsTheComputer' ? 'computer' :
                     this.shadowRoot.getElementById('gameId').value;

      const baseUrl = window.location.href.split('?')[0];
      window.location.href = `${baseUrl}?gameId=${gameId}`;
    });

    // Move selection
    const movesDrawerBody = this.shadowRoot.getElementById('movesDrawerBody');
    movesDrawerBody?.addEventListener('click', (e) => {
      if (this.stateManager.ui.animating) return;

      const button = e.target.closest('.move-card-button');
      if (button && !button.disabled) {
        const moveId = button.dataset.id;

        // Submit move to engine
        if (this.stateManager.game && moveId) {
          this.networkAdapter.submitMove(moveId);
          this.modalManager.closeDrawer();
        }
      }
    });

    // Show moves drawer
    const movesButton = this.shadowRoot.querySelector('.move-button-wrapper button');
    movesButton?.addEventListener('click', () => {
      this.uiRenderer.renderMoveSelection();
      this.modalManager.openDrawer();
    });

    // Copy share link
    this.shadowRoot.getElementById('copyShareLink')?.addEventListener('click', (e) => {
      e.preventDefault();
      const shareLinkInput = this.shadowRoot.getElementById('shareLink');
      const gameId = shareLinkInput?.value;
      if (gameId) {
        navigator.clipboard.writeText(gameId).then(() => {
          const originalValue = shareLinkInput.value;
          shareLinkInput.value = 'Copied!';
          setTimeout(() => {
            shareLinkInput.value = originalValue;
          }, 2000);
        });
      }
    });

    // Listen for round completion
    this.stateManager.onRoundComplete(async (roundData) => {
      // Skip animation and locking for round 0 (encounter start)
      if (roundData.number === 0) {
        // Just add to combat log
        this.combatLogRenderer.addRoundToLog(roundData);
        return;
      }

      // Set animating flag
      this.stateManager.ui.animating = true;

      try {
        // Play animation sequence
        await this.animationController.playRoundSequence(roundData.results);

        // Add to combat log
        this.combatLogRenderer.addRoundToLog(roundData);

        // Update mini-logs
        this.uiRenderer.renderMiniLog(true);
        this.uiRenderer.renderMiniLog(false);

        // Save game state for computer games
        if (this.stateManager.gameMode === 'computer') {
          this._saveGameState();
        }

        // Check for game over
        if (this.stateManager.isGameOver()) {
          this._handleGameOver();
        }
      } finally {
        this.stateManager.ui.animating = false;
      }
    });
  }

  _handleGameOver() {
    const winner = this.stateManager.getWinner();

    if (winner === 'player') {
      const victoryFlavor = randomChoice(flavorTextData.victory);
      const message = `
        <em style="opacity: 0.9;">${victoryFlavor}</em><br><br>
        <strong>🎉 VICTORY!</strong><br>
        Your opponent falls before you!<br>
        <a class='btn btn-sm btn-light mt-2' href='/'>⚔️ New Game</a>
      `;
      this._showGameEndMessage(message, 'bg-success');

      // Mark opponent as defeated
      const opponentCard = this.shadowRoot.querySelector('#opponentsCharacterCard');
      setTimeout(() => opponentCard?.classList.add('defeated'), 500);
    } else {
      const defeatFlavor = randomChoice(flavorTextData.defeat);
      const message = `
        <em style="opacity: 0.9;">${defeatFlavor}</em><br><br>
        <strong>💀 DEFEAT!</strong><br>
        You have been bested in combat.<br>
        <a class='btn btn-sm btn-light mt-2' href='/'>⚔️ New Game</a>
      `;
      this._showGameEndMessage(message, 'bg-danger');

      // Mark player as defeated
      const myCard = this.shadowRoot.querySelector('#myCharacterCard');
      setTimeout(() => myCard?.classList.add('defeated'), 500);
    }

    // Clear saved game state
    this._clearGameState();
  }

  _showGameEndMessage(message, bgClass) {
    const alertDiv = this.shadowRoot.querySelector('.my-moves-wrapper .alert');
    if (alertDiv) {
      alertDiv.innerHTML = message;
      alertDiv.className = `alert ${bgClass} text-white`;
    }
  }

  /**
   * Save current game state to localStorage (computer games only)
   */
  _saveGameState() {
    try {
      if (!this.stateManager.game) return;

      const state = {
        myCharacter: {
          health: this.stateManager.game.myCharacter.health,
          weapon: this.stateManager.game.myCharacter.weapon,
          shield: this.stateManager.game.myCharacter.shield
        },
        opponentCharacter: {
          health: this.stateManager.game.opponentsCharacter.health,
          weapon: this.stateManager.game.opponentsCharacter.weapon,
          shield: this.stateManager.game.opponentsCharacter.shield
        },
        roundHistory: this.stateManager.roundHistory
      };

      localStorage.setItem(this.stateManager.gameId, JSON.stringify(state));
      localStorage.setItem(CONFIG.STORAGE_KEYS.GAME_IN_PROGRESS, 'true');
    } catch (error) {
      console.error('Failed to save game state:', error);
    }
  }

  /**
   * Clear saved game state from localStorage
   */
  _clearGameState() {
    localStorage.removeItem(CONFIG.STORAGE_KEYS.GAME_IN_PROGRESS);
    localStorage.removeItem(CONFIG.STORAGE_KEYS.COMPUTER_OPPONENT);

    if (this.stateManager.gameId) {
      localStorage.removeItem(this.stateManager.gameId);
    }

    if (window.logging) {
      console.log('Cleared game state');
    }
  }
}

// ============================================================================
// Register Custom Element
// ============================================================================

if ('customElements' in window) {
  window.customElements.define('sword-fight', SwordFight);
}
